import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  AdminAccountTransaction,
  AdminApiKeySummary,
  GetAccountDetailResponse,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useAccount,
  useAdjustBalance,
  useAdminApiKeys,
  useMarkEmailVerified,
  useRevokeAdminApiKey,
  useResendEmailVerification,
} from '../api/useAdmin.ts';
import { AccountLifecycleCard } from '../components/AccountLifecycleCard.tsx';
import { formatUsdCents, parseUsdInputToCents } from '../components/formatters.ts';
import { Modal } from '../components/Modal.tsx';
import { OrgRoutingPolicyCard } from '../components/OrgRoutingPolicyCard.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS, INPUT_CLASS_MONO_ON_HIGHEST } from '../styles/forms.ts';

/**
 * `/customers/accounts/:accountId` — detail view with balance adjustment.
 *
 * DESIGN.md §5+§6: three stacked `surface-container` cards —
 *   1. Summary       — account + org + balance (large number in `primary`).
 *   2. Transactions  — last 20 `balance_transactions` with signed, colored
 *      amounts (green on credit, error on debit).
 *   3. Adjustment    — form → type-to-confirm modal → POST.
 *
 * Money-safety on the adjustment form:
 *   - Amount accepts a leading `-` for debits (intentional; negative
 *     adjustments are allowed to drive the balance below zero).
 *   - Submit opens a confirmation modal that shows target email + current
 *     balance + amount (color coded) + resulting balance.
 *   - The modal requires the operator to TYPE the amount (with the sign)
 *     exactly to enable Submit — same Wave 5C pattern as gift-card
 *     redeem-on-behalf.
 *   - `external_request_id` is minted at modal mount via
 *     `crypto.randomUUID()` so a button-click replay dedupes idempotently
 *     downstream.
 *
 * Spoof-resistance: the client NEVER sends `actor_user_id`. ops-api
 * extracts it from the session cookie and overrides any client value.
 */

const MAX_RECENT_TRANSACTIONS = 20;

function formatSigned(amount: number): string {
  return formatUsdCents(amount, true);
}

function formatIso(iso: string): string {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return iso;
  return new Date(parsed).toISOString().replace('T', ' ').slice(0, 19) + 'Z';
}

export function AccountDetail(): JSX.Element {
  const t = useT();
  const params = useParams<{ accountId: string }>();
  const accountId = params.accountId ?? '';
  const account = useAccount(accountId);

  if (account.isPending) {
    return (
      <section className="flex flex-col gap-8" data-testid="account-detail-loading">
        <div className="h-8 w-64 animate-pulse rounded-lg bg-surface-container" aria-hidden />
        <div className="h-32 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <div className="h-48 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <span className="sr-only">{t('account_detail.loading_aria')}</span>
      </section>
    );
  }

  if (account.isError) {
    return (
      <section
        className="flex flex-col gap-4 rounded-xl bg-error-container p-6 text-on-error-container"
        role="alert"
        data-testid="account-detail-error"
      >
        <h2 className="text-base font-semibold">{t('account_detail.error.title')}</h2>
        <p className="text-sm">
          {account.error instanceof OpsApiError
            ? `${account.error.code}: ${account.error.message}`
            : t('common.network_error')}
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void account.refetch()}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('common.retry')}
          </button>
          <Link to="/customers/accounts" className="text-[11px] font-bold uppercase tracking-[0.08em]">
            {t('account_detail.back')}
          </Link>
        </div>
      </section>
    );
  }

  return <AccountDetailReady account={account.data} />;
}

interface ReadyProps {
  readonly account: GetAccountDetailResponse;
}

function AccountDetailReady({ account }: ReadyProps): JSX.Element {
  const t = useT();
  return (
    <section className="flex flex-col gap-8" data-testid="account-detail-page">
      <nav aria-label="breadcrumb" className="flex items-center gap-2 text-[11px] text-outline">
        <Link to="/customers/accounts" className="hover:text-primary">
          {t('account_detail.breadcrumb.accounts')}
        </Link>
        <span aria-hidden>/</span>
        <span className="font-semibold text-on-surface" data-testid="account-breadcrumb-email">
          {account.email}
        </span>
      </nav>

      <header className="flex flex-col gap-3">
        <h2
          className="text-3xl font-semibold tracking-tight text-on-surface"
          data-testid="account-detail-title"
        >
          {account.email}
        </h2>
        <div className="flex flex-wrap items-center gap-3 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {account.is_system ? (
            <StatusChip
              variant="neutral"
              label={t('accounts.chip.system')}
              testId="account-detail-system"
            />
          ) : (
            <StatusChip
              variant="ok"
              label={t('accounts.chip.tenant')}
              testId="account-detail-system"
            />
          )}
          <span aria-hidden>·</span>
          <span data-testid="account-detail-created">
            {t('account_detail.meta.created', { iso: formatIso(account.created_at) })}
          </span>
          {account.org !== null ? (
            <>
              <span aria-hidden>·</span>
              <span data-testid="account-detail-org-label">
                {t('account_detail.meta.org', {
                  name: account.org.name,
                  role: account.org.member_role,
                })}
              </span>
            </>
          ) : null}
        </div>
      </header>

      <AccountDetailTabs account={account} />
      <SummaryCard account={account} />
      <EmailVerificationCard account={account} />
      {account.is_system ? null : (
        <AccountLifecycleCard
          accountId={account.account_id}
          email={account.email}
          suspendedAt={account.suspended_at}
          suspensionReason={account.suspension_reason}
          deletedAt={account.deleted_at}
        />
      )}
      <ApiKeysCard orgId={account.org?.org_id ?? null} />
      <TransactionsCard transactions={account.recent_transactions} />
      <AdjustmentCard account={account} />
      {account.org !== null ? (
        <OrgRoutingPolicyCard
          orgId={account.org.org_id}
          orgName={account.org.name}
        />
      ) : null}
    </section>
  );
}

function AccountDetailTabs({ account }: ReadyProps): JSX.Element {
  const usageHref = `/customers/usage?accountId=${encodeURIComponent(account.account_id)}`;
  const requestsHref = `/customers/requests?accountId=${encodeURIComponent(account.account_id)}`;
  return (
    <nav
      aria-label="Account detail sections"
      className="flex flex-wrap gap-2 rounded-xl bg-surface-container p-2"
      data-testid="account-detail-tabs"
    >
      <span className="rounded-lg bg-primary px-3 py-2 text-xs font-bold text-on-primary">概要</span>
      <Link className="rounded-lg px-3 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high" to={usageHref}>
        用量
      </Link>
      <Link className="rounded-lg px-3 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high" to={requestsHref}>
        请求日志
      </Link>
      <a className="rounded-lg px-3 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high" href="#account-api-keys">
        API 密钥
      </a>
      <a className="rounded-lg px-3 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high" href="#account-transactions">
        账务交易
      </a>
      <a className="rounded-lg px-3 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high" href="#account-support-actions">
        支持动作
      </a>
    </nav>
  );
}

// ─── Summary card ────────────────────────────────────────────────────────

interface SummaryCardProps {
  readonly account: GetAccountDetailResponse;
}

function SummaryCard({ account }: SummaryCardProps): JSX.Element {
  const t = useT();
  return (
    <article
      data-testid="account-summary-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('account_detail.summary.title')}
        </h3>
      </header>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <Field
          label={t('account_detail.summary.account_id')}
          value={account.account_id}
          mono
          testId="account-summary-id"
        />
        <Field
          label={t('account_detail.summary.org_id')}
          value={account.org?.org_id ?? '—'}
          mono
          testId="account-summary-org-id"
        />
        <Field
          label={t('account_detail.summary.org_name')}
          value={account.org?.name ?? '—'}
          testId="account-summary-org-name"
        />
        <Field
          label={t('account_detail.summary.member_role')}
          value={account.org?.member_role ?? '—'}
          mono
          testId="account-summary-member-role"
        />
      </div>
      <div
        className="flex flex-col gap-2 rounded-lg bg-surface-container-low p-6"
        data-testid="account-summary-balance-block"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
          {t('account_detail.summary.current_balance')}
        </span>
        <span
          className="font-mono text-4xl font-bold tracking-tight text-primary"
          data-testid="account-summary-balance"
        >
          {formatUsdCents(account.balance_usd_cents)}
        </span>
      </div>
    </article>
  );
}

function EmailVerificationCard({ account }: SummaryCardProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const resend = useResendEmailVerification();
  const markVerified = useMarkEmailVerified();
  const [reason, setReason] = useState('');
  const verified = account.email_verified === true;

  async function handleResend(): Promise<void> {
    try {
      await resend.mutateAsync({
        accountId: account.account_id,
        body: { reason: 'support resend' },
      });
      toast.success(t('account_detail.email_verification.toast.resent'));
    } catch (err) {
      toast.error(err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err));
    }
  }

  async function handleMarkVerified(): Promise<void> {
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      toast.error(t('account_detail.email_verification.error.reason_required'));
      return;
    }
    try {
      await markVerified.mutateAsync({
        accountId: account.account_id,
        body: { reason: trimmed },
      });
      setReason('');
      toast.success(t('account_detail.email_verification.toast.marked'));
    } catch (err) {
      toast.error(err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err));
    }
  }

  return (
    <article
      id="account-support-actions"
      data-testid="account-email-verification-card"
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('account_detail.email_verification.title')}
          </h3>
          <p className="text-xs text-outline">{t('account_detail.email_verification.body')}</p>
        </div>
        <StatusChip
          variant={verified ? 'ok' : 'warning'}
          label={
            verified
              ? t('account_detail.email_verification.status.verified')
              : t('account_detail.email_verification.status.unverified')
          }
          testId="account-email-verification-status"
        />
      </header>
      <div className="flex flex-wrap items-end gap-3">
        <button
          type="button"
          onClick={() => void handleResend()}
          disabled={verified || resend.isPending || markVerified.isPending}
          data-testid="account-email-verification-resend"
          className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:pointer-events-none disabled:opacity-40"
        >
          {resend.isPending
            ? t('account_detail.email_verification.resending')
            : t('account_detail.email_verification.resend')}
        </button>
        <label className="min-w-64 flex-1">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('account_detail.email_verification.reason_label')}
          </span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            disabled={verified || resend.isPending || markVerified.isPending}
            placeholder={t('account_detail.email_verification.reason_placeholder')}
            data-testid="account-email-verification-reason"
            className={INPUT_CLASS}
          />
        </label>
        <button
          type="button"
          onClick={() => void handleMarkVerified()}
          disabled={verified || resend.isPending || markVerified.isPending}
          data-testid="account-email-verification-mark"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:pointer-events-none disabled:opacity-40"
        >
          {markVerified.isPending
            ? t('account_detail.email_verification.marking')
            : t('account_detail.email_verification.mark_verified')}
        </button>
        <Link
          to={`/customers/requests?accountId=${encodeURIComponent(account.account_id)}`}
          data-testid="account-support-requests-link"
          className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
        >
          {t('account_detail.email_verification.support_link')}
        </Link>
      </div>
    </article>
  );
}

interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly testId?: string;
}

function Field({ label, value, mono, testId }: FieldProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {label}
      </span>
      <span
        className={`break-all text-sm text-on-surface ${mono === true ? 'font-mono' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Transactions card ───────────────────────────────────────────────────

interface TransactionsCardProps {
  readonly transactions: readonly AdminAccountTransaction[];
}

function TransactionsCard({ transactions }: TransactionsCardProps): JSX.Element {
  const t = useT();
  const rows = transactions.slice(0, MAX_RECENT_TRANSACTIONS);
  return (
    <article
      id="account-transactions"
      data-testid="account-transactions-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('account_detail.tx.title')}
        </h3>
        <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
          {t('account_detail.tx.last_n', { n: String(MAX_RECENT_TRANSACTIONS) })}
        </span>
      </header>
      {rows.length === 0 ? (
        <p className="text-sm text-outline" data-testid="account-transactions-empty">
          {t('account_detail.tx.empty')}
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg bg-surface-container-low">
          <table
            className="w-full border-separate border-spacing-0"
            data-testid="account-transactions-table"
          >
            <thead>
              <tr className="bg-surface-container-high">
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                >
                  {t('account_detail.tx.column.type')}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                >
                  {t('account_detail.tx.column.amount')}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                >
                  {t('account_detail.tx.column.actor')}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                >
                  {t('account_detail.tx.column.reason')}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                >
                  {t('account_detail.tx.column.created')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((tx, i) => {
                const zebra =
                  i % 2 === 0 ? 'bg-surface-container-low' : 'bg-surface-container';
                const amountClass =
                  tx.amount_usd_cents > 0
                    ? 'text-primary'
                    : tx.amount_usd_cents < 0
                      ? 'text-error'
                      : 'text-on-surface-variant';
                return (
                  <tr
                    key={tx.id}
                    data-testid={`account-transaction-row-${tx.id}`}
                    className={`${zebra} transition-colors hover:bg-surface-bright`}
                  >
                    <td className="px-4 py-3 font-mono text-[12px] text-on-surface-variant">
                      {tx.type}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-mono text-[13px] font-semibold ${amountClass}`}
                      data-testid={`account-transaction-row-${tx.id}-amount`}
                    >
                      {formatSigned(tx.amount_usd_cents)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusChip
                        variant="neutral"
                        label={tx.actor_type}
                        testId={`account-transaction-row-${tx.id}-actor`}
                      />
                    </td>
                    <td className="px-4 py-3 text-[12px] text-on-surface">
                      {tx.reason ?? '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-on-surface-variant">
                      {formatIso(tx.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

// ─── Adjustment card + confirmation modal ────────────────────────────────

interface AdjustmentCardProps {
  readonly account: GetAccountDetailResponse;
}

interface DraftAdjustment {
  readonly amountUsdCents: number;
  readonly amountRaw: string;
  readonly reason: string;
}

/**
 * Finance preset modes on top of the generic signed adjustment (Epic
 * #109 / P1.8). `custom` keeps the pre-existing behaviour (signed amount
 * + free-form reason). `refund` and `sla_credit` constrain the amount to
 * positive values and auto-prefix the reason so
 * `balance_transactions.reason LIKE 'refund:%'` / `'sla_credit:%'` lands
 * as a clean category for the monthly close report — the backend ledger
 * is untouched; finance just gets a structured prefix on the text side.
 */
type AdjustmentMode = 'custom' | 'refund' | 'sla_credit';

function reasonPrefix(mode: AdjustmentMode): string {
  if (mode === 'refund') return 'refund: ';
  if (mode === 'sla_credit') return 'sla_credit: ';
  return '';
}

function AdjustmentCard({ account }: AdjustmentCardProps): JSX.Element {
  const t = useT();
  const [mode, setMode] = useState<AdjustmentMode>('custom');
  const [amountRaw, setAmountRaw] = useState('');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftAdjustment | null>(null);

  function switchMode(next: AdjustmentMode): void {
    setMode(next);
    setFormError(null);
    // Wipe the reason / amount when switching modes — carrying over a
    // negative amount from `custom` into `refund` would silently fail
    // the positive-amount gate, and keeping a prior `refund: X` reason
    // when flipping to `sla_credit` would land the wrong prefix.
    setReason('');
    setAmountRaw('');
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const trimmedAmount = amountRaw.trim();
    if (trimmedAmount.length === 0) {
      setFormError(t('account_detail.adjust.error.amount_required'));
      return;
    }
    const parsed = parseUsdInputToCents(trimmedAmount);
    if (!parsed.ok || parsed.cents === 0) {
      setFormError(t('account_detail.adjust.error.amount_non_zero'));
      return;
    }
    if ((mode === 'refund' || mode === 'sla_credit') && parsed.cents <= 0) {
      setFormError(t('account_detail.adjust.error.positive_required'));
      return;
    }
    const trimmedReason = reason.trim();
    if (trimmedReason.length === 0) {
      setFormError(t('account_detail.adjust.error.reason_required'));
      return;
    }
    const prefixed = `${reasonPrefix(mode)}${trimmedReason}`;
    setDraft({ amountUsdCents: parsed.cents, amountRaw: parsed.normalized, reason: prefixed });
  }

  function handleConfirmClose(success: boolean): void {
    setDraft(null);
    if (success) {
      setAmountRaw('');
      setReason('');
    }
  }

  return (
    <article
      data-testid="account-adjust-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('account_detail.adjust.title')}
        </h3>
        <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
          {t('account_detail.adjust.subtitle')}
        </span>
      </header>
      <div
        role="radiogroup"
        aria-label={t('account_detail.adjust.mode.aria_label')}
        data-testid="account-adjust-mode"
        className="flex flex-wrap items-center gap-2 rounded-lg bg-surface-container-low p-2"
      >
        {(['custom', 'refund', 'sla_credit'] as const).map((m) => {
          const active = mode === m;
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => switchMode(m)}
              data-testid={`account-adjust-mode-${m}`}
              data-active={active ? 'true' : 'false'}
              className={`inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container ${
                active
                  ? 'bg-primary-container text-on-primary'
                  : 'bg-surface-container-highest text-on-surface hover:bg-surface-bright'
              }`}
            >
              {t(
                m === 'custom'
                  ? 'account_detail.adjust.mode.custom'
                  : m === 'refund'
                  ? 'account_detail.adjust.mode.refund'
                  : 'account_detail.adjust.mode.sla_credit',
              )}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-outline" data-testid="account-adjust-mode-hint">
        {t(
          mode === 'custom'
            ? 'account_detail.adjust.mode.custom.hint'
            : mode === 'refund'
            ? 'account_detail.adjust.mode.refund.hint'
            : 'account_detail.adjust.mode.sla_credit.hint',
        )}
      </p>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('account_detail.adjust.amount.label')}
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={amountRaw}
              onChange={(e) => setAmountRaw(e.currentTarget.value)}
              placeholder={t('account_detail.adjust.amount.placeholder')}
              data-testid="account-adjust-amount"
              className={INPUT_CLASS}
              autoComplete="off"
            />
          </label>
          <label className="flex flex-col gap-2 sm:row-span-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('account_detail.adjust.reason.label')}
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              rows={3}
              placeholder={t(
                mode === 'refund'
                  ? 'account_detail.adjust.reason.placeholder_refund'
                  : mode === 'sla_credit'
                  ? 'account_detail.adjust.reason.placeholder_sla_credit'
                  : 'account_detail.adjust.reason.placeholder',
              )}
              data-testid="account-adjust-reason"
              className={`${INPUT_CLASS} min-h-[72px] resize-y`}
            />
          </label>
        </div>
        {formError !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="account-adjust-error"
          >
            {formError}
          </p>
        ) : null}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            data-testid="account-adjust-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            {t('account_detail.adjust.submit')}
          </button>
        </div>
      </form>

      {draft !== null ? (
        <AdjustConfirmModal
          account={account}
          draft={draft}
          onClose={(success) => handleConfirmClose(success)}
        />
      ) : null}
    </article>
  );
}

interface AdjustConfirmModalProps {
  readonly account: GetAccountDetailResponse;
  readonly draft: DraftAdjustment;
  readonly onClose: (success: boolean) => void;
}

function AdjustConfirmModal({
  account,
  draft,
  onClose,
}: AdjustConfirmModalProps): JSX.Element {
  const t = useT();
  const adjust = useAdjustBalance();
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  // Mint a fresh idempotency key for this modal instance. useMemo is not
  // stable across remounts (we WANT a fresh key if the operator reopens the
  // modal) but stable for the life of this modal — a double-click on the
  // submit button will therefore replay the same external_request_id.
  const externalRequestId = useMemo(() => crypto.randomUUID(), []);

  // `typed` must match the raw amount the operator drafted (with sign).
  const typedMatches = typed.trim() === draft.amountRaw;
  const amountClass = draft.amountUsdCents > 0 ? 'text-primary' : 'text-error';
  const resultingBalance = account.balance_usd_cents + draft.amountUsdCents;
  const resultingClass = resultingBalance < 0 ? 'text-error' : 'text-on-surface';

  useEffect(() => {
    setServerError(null);
  }, [typed]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setServerError(null);
    if (!typedMatches) {
      setServerError(t('account_detail.confirm.type_to_confirm', { raw: draft.amountRaw }));
      return;
    }
    try {
      await adjust.mutateAsync({
        accountId: account.account_id,
        body: {
          amount_usd_cents: draft.amountUsdCents,
          reason: draft.reason,
          external_request_id: externalRequestId,
        },
      });
      onClose(true);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setServerError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!adjust.isPending) onClose(false);
      }}
      titleId="adjust-confirm-title"
      testId="account-adjust-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2
          id="adjust-confirm-title"
          className="text-base font-semibold text-on-surface"
        >
          {t('account_detail.confirm.title')}
        </h2>
        <div className="flex flex-col gap-3 rounded-lg bg-surface-container-low p-4 text-sm text-outline">
          <Row
            label={t('account_detail.confirm.target_account')}
            value={account.email}
            testId="account-adjust-modal-email"
          />
          <Row
            label={t('account_detail.confirm.current_balance')}
            value={formatUsdCents(account.balance_usd_cents)}
            mono
            testId="account-adjust-modal-current"
          />
          <Row
            label={t('account_detail.confirm.adjustment')}
            value={formatSigned(draft.amountUsdCents)}
            mono
            valueClass={amountClass}
            testId="account-adjust-modal-delta"
          />
          <Row
            label={t('account_detail.confirm.resulting_balance')}
            value={formatUsdCents(resultingBalance)}
            mono
            valueClass={resultingClass}
            testId="account-adjust-modal-result"
          />
        </div>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('account_detail.confirm.type_to_confirm', { raw: draft.amountRaw })}
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            placeholder={draft.amountRaw}
            data-testid="account-adjust-modal-typed"
            disabled={adjust.isPending}
            className={INPUT_CLASS_MONO_ON_HIGHEST}
            autoComplete="off"
          />
        </label>
        <p className="text-[10px] uppercase tracking-[0.1em] text-outline">
          {t('account_detail.confirm.idempotency_key')}{' '}
          <span
            className="font-mono normal-case tracking-normal text-on-surface-variant"
            data-testid="account-adjust-modal-request-id"
          >
            {externalRequestId}
          </span>
        </p>
        {serverError !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="account-adjust-modal-error"
          >
            {serverError}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              if (!adjust.isPending) onClose(false);
            }}
            disabled={adjust.isPending}
            data-testid="account-adjust-modal-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={adjust.isPending || !typedMatches}
            data-testid="account-adjust-modal-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
          >
            {adjust.isPending
              ? t('account_detail.confirm.submitting')
              : t('account_detail.confirm.submit', { signed: formatSigned(draft.amountUsdCents) })}
          </button>
        </div>
      </form>
    </Modal>
  );
}

interface RowProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly valueClass?: string;
  readonly testId?: string;
}

function Row({ label, value, mono, valueClass, testId }: RowProps): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {label}
      </span>
      <span
        className={`text-sm font-semibold ${mono === true ? 'font-mono' : ''} ${
          valueClass ?? 'text-on-surface'
        }`}
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}

// ─── API keys card ───────────────────────────────────────────────────────
//
// Lists the org's api_keys (Epic #109 / P0.2). Active rows surface a
// Revoke button that opens a confirmation modal requiring a written
// reason — the reason is attached to the audit row and captured in
// ops_audit.metadata. Revoked rows stay in the list with a muted chip
// so the whole lifecycle stays visible on one page.

interface ApiKeysCardProps {
  readonly orgId: string | null;
}

function ApiKeysCard({ orgId }: ApiKeysCardProps): JSX.Element | null {
  const t = useT();
  const keys = useAdminApiKeys(orgId ?? undefined);

  // Account with no org (e.g. acc_system pre-org backfill) — hide the
  // card entirely. Rendering an empty card with "no keys" feels like
  // a bug; the account simply has no key-owning scope.
  if (orgId === null) {
    return null;
  }

  return (
    <article
      id="account-api-keys"
      data-testid="account-api-keys-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('account_detail.api_keys.title')}
        </h3>
        <span className="text-[10px] text-outline">
          {t('account_detail.api_keys.subtitle')}
        </span>
      </header>

      {keys.isPending ? (
        <p
          className="text-xs text-outline"
          role="status"
          data-testid="account-api-keys-loading"
        >
          {t('account_detail.api_keys.loading')}
        </p>
      ) : null}

      {keys.isError ? (
        <p
          className="text-xs text-error"
          role="alert"
          data-testid="account-api-keys-error"
        >
          {keys.error instanceof OpsApiError
            ? `${keys.error.code}: ${keys.error.message}`
            : t('account_detail.api_keys.load_error')}
        </p>
      ) : null}

      {keys.data !== undefined && keys.data.keys.length === 0 ? (
        <p
          className="text-xs text-outline"
          data-testid="account-api-keys-empty"
        >
          {t('account_detail.api_keys.empty')}
        </p>
      ) : null}

      {keys.data !== undefined && keys.data.keys.length > 0 ? (
        <div className="overflow-x-auto" data-testid="account-api-keys-table">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-outline">
                <th className="px-3 py-2 font-semibold">
                  {t('account_detail.api_keys.col.name')}
                </th>
                <th className="px-3 py-2 font-semibold">
                  {t('account_detail.api_keys.col.prefix')}
                </th>
                <th className="px-3 py-2 font-semibold">
                  {t('account_detail.api_keys.col.created_at')}
                </th>
                <th className="px-3 py-2 font-semibold">
                  {t('account_detail.api_keys.col.last_used')}
                </th>
                <th className="px-3 py-2 font-semibold">
                  {t('account_detail.api_keys.col.status')}
                </th>
                <th className="px-3 py-2 font-semibold">
                  {t('account_detail.api_keys.col.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {keys.data.keys.map((k) => (
                <ApiKeyRow key={k.id} keyRow={k} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </article>
  );
}

function ApiKeyRow({ keyRow }: { readonly keyRow: AdminApiKeySummary }): JSX.Element {
  const t = useT();
  const [modalOpen, setModalOpen] = useState(false);
  const isRevoked = keyRow.revoked_at !== null;
  return (
    <tr
      className="odd:bg-surface-container-low"
      data-testid={`account-api-key-row-${keyRow.id}`}
    >
      <td className="px-3 py-2 text-on-surface">
        {keyRow.name ?? t('account_detail.api_keys.unnamed')}
      </td>
      <td className="px-3 py-2 font-mono text-on-surface-variant">
        {keyRow.key_prefix ?? '—'}
      </td>
      <td className="px-3 py-2 font-mono text-on-surface-variant">
        {formatIso(keyRow.created_at)}
      </td>
      <td className="px-3 py-2 font-mono text-on-surface-variant">
        {keyRow.last_used_at !== null ? formatIso(keyRow.last_used_at) : '—'}
      </td>
      <td className="px-3 py-2">
        {isRevoked ? (
          <StatusChip
            variant="error"
            label={t('account_detail.api_keys.status.revoked')}
            testId={`account-api-key-chip-${keyRow.id}`}
          />
        ) : (
          <StatusChip
            variant="ok"
            label={t('account_detail.api_keys.status.active')}
            testId={`account-api-key-chip-${keyRow.id}`}
          />
        )}
      </td>
      <td className="px-3 py-2">
        {isRevoked ? (
          <span className="text-[10px] text-outline">
            {t('account_detail.api_keys.revoked_meta', {
              iso: formatIso(keyRow.revoked_at ?? ''),
            })}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid={`account-api-key-revoke-${keyRow.id}`}
            className="inline-flex items-center justify-center rounded-lg bg-error-container px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-on-error-container transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            {t('account_detail.api_keys.revoke')}
          </button>
        )}
        {modalOpen ? (
          <RevokeApiKeyModal
            keyRow={keyRow}
            onClose={() => setModalOpen(false)}
          />
        ) : null}
      </td>
    </tr>
  );
}

function RevokeApiKeyModal({
  keyRow,
  onClose,
}: {
  readonly keyRow: AdminApiKeySummary;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const revoke = useRevokeAdminApiKey();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reasonTrimmed = reason.trim();
  const canSubmit = reasonTrimmed.length > 0 && !revoke.isPending;

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      const res = await revoke.mutateAsync({
        id: keyRow.id,
        body: { reason: reasonTrimmed },
      });
      toast.success(
        res.changed
          ? t('account_detail.api_keys.toast.revoked', { id: keyRow.id })
          : t('account_detail.api_keys.toast.already_revoked', { id: keyRow.id }),
      );
      onClose();
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  const titleId = `revoke-api-key-title-${keyRow.id}`;
  return (
    <Modal
      isOpen
      onClose={revoke.isPending ? () => undefined : onClose}
      titleId={titleId}
      testId={`account-api-key-revoke-modal-${keyRow.id}`}
    >
      <form
        className="flex flex-col gap-4 p-8"
        onSubmit={(e) => void handleSubmit(e)}
        noValidate
      >
        <header className="flex flex-col gap-1">
          <h2 id={titleId} className="text-base font-semibold">
            {t('account_detail.api_keys.revoke_modal.title', {
              name: keyRow.name ?? keyRow.id,
            })}
          </h2>
          <p className="text-xs text-outline">
            {t('account_detail.api_keys.revoke_modal.body')}
          </p>
          <p className="font-mono text-[10px] text-outline-variant">
            {keyRow.id}
          </p>
        </header>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('account_detail.api_keys.revoke_modal.reason.label')}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            disabled={revoke.isPending}
            required
            rows={3}
            data-testid={`account-api-key-revoke-reason-${keyRow.id}`}
            className={`${INPUT_CLASS} resize-none`}
            placeholder={t('account_detail.api_keys.revoke_modal.reason.placeholder')}
          />
        </label>
        {error !== null ? (
          <p
            className="text-xs text-error"
            role="alert"
            data-testid={`account-api-key-revoke-error-${keyRow.id}`}
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={revoke.isPending}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid={`account-api-key-revoke-submit-${keyRow.id}`}
            className="inline-flex items-center justify-center rounded-lg bg-error-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-error-container transition-all hover:brightness-110 disabled:opacity-40 disabled:pointer-events-none"
          >
            {revoke.isPending
              ? t('account_detail.api_keys.revoke_modal.submitting')
              : t('account_detail.api_keys.revoke_modal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
